import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  TenantSummaryDto,
  TenantsPaginationSummaryDto,
} from '@tdms/tdms-next-api';
import {
  DefaultResponse,
  Endpoint,
  IntegrationUpsertPayload,
  PossibleIntegrationTypes,
  Datasource,
  TenantsOverview,
  TenantIntegrations,
} from '@mercedes-benz/pmas-common';
import { submitRequest } from './submitRequest';

export class IntegrationsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  async getTenants(): Promise<TenantSummaryDto[]> {
    const url = await this.discoveryApi.getBaseUrl('tdms-next');
    const results: TenantSummaryDto[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const response = await this.fetchApi.fetch(
        `${url}/tenants?tenantsStatus=ACTIVE&page=${page}&pageSize=25`,
      );
      const data: TenantsPaginationSummaryDto = await response.json();
      results.push(...(data.results ?? []));
      totalPages = data.pagination?.totalPages ?? 0;
      page++;
    } while (page <= totalPages);
    return results;
  }

  async getMemberTenants(mbagId: string): Promise<TenantSummaryDto[]> {
    const url = await this.discoveryApi.getBaseUrl('tdms-next');
    const results: TenantSummaryDto[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const response = await this.fetchApi.fetch(
        `${url}/tenants?tenantsStatus=ACTIVE&searchQuery=${encodeURIComponent(mbagId)}&page=${page}&pageSize=25`,
      );
      const data: TenantsPaginationSummaryDto = await response.json();
      results.push(...(data.results ?? []));
      totalPages = data.pagination?.totalPages ?? 0;
      page++;
    } while (page <= totalPages);
    return results;
  }

  async getAllTenantOverview(): Promise<DefaultResponse<TenantsOverview[]>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/tenant/overview/all`,
    });
  }

  async getTenantIntegrations(
    name: string,
  ): Promise<DefaultResponse<TenantIntegrations>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/tenant/${name}`,
    });
  }

  async enableTenantIntegrations(
    name: string,
    status: string,
  ): Promise<DefaultResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: `/tenant/${name}/${status}`,
    });
  }

  async getIntegrations(
    integrationType: Endpoint,
  ): Promise<DefaultResponse<PossibleIntegrationTypes>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/integrations/${integrationType}`,
    });
  }

  async getIntegration(
    integrationId: number,
    integrationType: Endpoint,
  ): Promise<DefaultResponse<PossibleIntegrationTypes>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/integrations/${integrationType}/${integrationId}`,
    });
  }

  async getIntegrationsPerTenant<T = PossibleIntegrationTypes>(
    tenant: string,
    integrationType: Endpoint,
  ): Promise<DefaultResponse<T>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/integrations/${integrationType}/tenant/${tenant}`,
    });
  }

  async getDatasources(
    integrationType: Endpoint,
  ): Promise<DefaultResponse<Datasource[]>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/integrations/${integrationType}/datasources`,
    });
  }

  async createIntegration(
    integrationType: Endpoint,
    integration: IntegrationUpsertPayload,
  ): Promise<DefaultResponse<PossibleIntegrationTypes>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: `/integrations/${integrationType}`,
      body: {
        integration: integration,
      },
    });
  }

  async updateIntegration(
    integrationType: Endpoint,
    integrationId: number,
    integration: IntegrationUpsertPayload,
  ): Promise<DefaultResponse<PossibleIntegrationTypes>> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'PUT',
      path: `/integrations/${integrationType}/${integrationId}`,
      body: {
        integration: integration,
      },
    });
  }

  async deleteIntegration(
    integrationId: number,
    integrationType: Endpoint,
  ): Promise<DefaultResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'DELETE',
      path: `/integrations/${integrationType}/${integrationId}`,
    });
  }
}
